import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Head from 'next/head';
import { usePathname } from 'next/navigation';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@savvy-kit/components/hover-card';
import { Skeleton } from '@savvy-kit/components/skeleton';
import { TAB_VARIANT_LINK, TAB_VARIANT_TOGGLE } from '@savvy-kit/components/tabs';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import HorizontalSlider from '@savvy-kit/patterns/horizontal-slider';
import LinkTabs from '@savvy-kit/patterns/link-tabs';
import ToggleTabs from '@savvy-kit/patterns/toggle-tabs';
import BreadcrumbSegments from '@savvy-kit/templates/breadcrumb-bar/breadcrumb-segments';
import { usePageHeaderSlot } from '@savvy-kit/templates/page-header-slot';
import { clamp } from 'es-toolkit';

import type { TabVariant } from '@savvy-kit/components/tabs';
import type { LinkTabsProps } from '@savvy-kit/patterns/link-tabs';
import type { ToggleTabsProps } from '@savvy-kit/patterns/toggle-tabs';
import type { BreadcrumbSegment } from '@savvy-kit/templates/breadcrumb-bar/breadcrumb-segments';

import { PageTitleCollapsedContext } from './page-title-collapsed-context';
import { useScrollProgressGuard } from './use-scroll-progress-guard';

type PageTitleProps = {
  readonly pageTitle: string;
  /** Optional secondary content rendered next to the H1 in muted style (e.g. masked account numbers). */
  readonly pageTitleSuffix?: ReactNode;
  readonly documentTitle?: string;
  readonly breadcrumbs?: readonly BreadcrumbSegment[];
  readonly collapsible?: boolean;
  /** Controls H1 visibility. true = static H1 (drill-down), false = H1 hidden (tab-level). Omit for default behavior. */
  readonly showTitle?: boolean;
  /** When true, render a skeleton in place of the H1 (for async-loaded page names). */
  readonly loading?: boolean;
  readonly tabsProps?: LinkTabsProps | ToggleTabsProps;
  readonly tabVariant?: TabVariant;
  /** Right of the tab row, outside the tabs' scroller and the tablist: holds the edge as the row scrolls. */
  readonly tabRowActions?: ReactNode;
  readonly actionButtons?: React.ReactNode;
  /** Optional content rendered below the H1 in the title area (e.g. a "Log in as" button).
   * Lives inside the collapse grid, so it fades out with the H1 and is invisible whenever
   * `showTitle` is false. Callers who need controls that survive the collapse want
   * `titleRowActions` instead — moving this slot would reveal it on pages that rely on it
   * staying hidden. */
  readonly pageTitleExtra?: ReactNode;
  /** Optional actions right-aligned in the breadcrumb row, outside the collapse grid, so they stay
   * reachable on tab-level pages and after scrolling. Mounted once for both collapse states —
   * read `useIsPageTitleCollapsed()` to render a compact form rather than expecting a second mount. */
  readonly titleRowActions?: ReactNode;
  /** Optional content in the breadcrumb row after the trail, for a compact form of something that
   * `belowHeader` shows in full while expanded. Takes the row's leftover width, so it must clip
   * itself rather than push the trail. Mounted once for both collapse states under the same
   * contract as `titleRowActions` — read `useIsPageTitleCollapsed()`, don't expect a second mount. */
  readonly breadcrumbRowExtra?: ReactNode;
  /** Collapses `belowHeader` along with the H1 instead of leaving it below the collapsed header.
   * Opt-in: a caller with no `breadcrumbRowExtra` counterpart would just lose the content on scroll. */
  readonly collapseBelowHeader?: boolean;
  readonly maxWidth?: string;
  readonly Icon?: LucideIcon;
  readonly iconHoverContent?: React.ReactNode;
  readonly description?: string;
  /** Optional accessory row rendered between the title header and the tab navigation —
   * e.g. an inline tag-management row on household pages. */
  readonly belowHeader?: ReactNode;
  readonly children?: ReactNode;
};

const PageTitle = forwardRef<HTMLDivElement, PageTitleProps>(function PageTitle(
  {
    pageTitle,
    pageTitleSuffix,
    documentTitle,
    breadcrumbs,
    collapsible,
    showTitle,
    loading = false,
    tabsProps,
    tabVariant = TAB_VARIANT_LINK,
    tabRowActions,
    actionButtons,
    pageTitleExtra,
    titleRowActions,
    breadcrumbRowExtra,
    collapseBelowHeader = false,
    maxWidth,
    Icon,
    iconHoverContent,
    description,
    belowHeader,
    children,
  },
  ref,
) {
  const headTitle = documentTitle || `${pageTitle} | Savvy`;
  const hasTabs = !!tabsProps?.tabs?.length;
  const pathname = usePathname();
  const headerSlot = usePageHeaderSlot();

  // Distance (in px of scroll) over which the H1 fades and the padding
  // contracts. Below 0 → fully expanded; above this → fully collapsed; in
  // between → linearly interpolated 1:1 with the user's scroll.
  const COLLAPSE_DISTANCE_PX = 80;
  // How long a detected scroll-offset/collapse chase (see `guardedCollapseProgress` below) is held
  // still before committing wherever it lands. Measured against real trackpad/keyboard scroll
  // bursts, which resolved within ~150ms of their first event.
  const COLLAPSE_CHASE_COOLDOWN_MS = 200;
  // Wrapper padding endpoints. Interpolated by `collapseProgress`.
  const PADDING_EXPANDED_PX = 16;
  const PADDING_COLLAPSED_PX = 8;
  // Half the top margin the H1 row carries, so the expanded actions centre on the H1's own line
  // rather than on the whole collapse block.
  const ACTIONS_TITLE_ROW_NUDGE_PX = 8;

  const computeScrollProgress = () => {
    if (typeof window === 'undefined') return 0;
    return clamp(window.scrollY / COLLAPSE_DISTANCE_PX, 0, 1);
  };

  // 0 = fully expanded, 1 = fully collapsed. Driving the collapse continuously
  // off scroll position (instead of flipping a boolean at a threshold) means
  // the bar tracks the scroll wheel 1:1 — no discrete "jump" mid-scroll.
  const [collapseProgress, setCollapseProgress] = useState(computeScrollProgress);

  // CSS transitions are armed only for non-scroll state changes (showTitle
  // overrides on household pages). Scroll-driven updates set this false so the
  // bar follows the user's input directly without lagging behind by 200ms.
  const [animateTransition, setAnimateTransition] = useState(false);

  // Collapsing removes real in-flow height from a `sticky top-0` header, so once scroll crosses the
  // threshold, the page's scrollable height shrinks while the browser's own scroll offset (mid
  // trackpad momentum, or a keyboard scroll under the host app's global `scroll-behavior: smooth`)
  // is still catching up to the same gesture — it clamps, that clamp reads back as a reversal, and
  // the header and the scroll offset chase each other for the rest of the burst. A normal, monotonic
  // scroll never reverses direction, so `useScrollProgressGuard` passes it through unchanged and the
  // collapse/expand animation stays exactly as responsive as raw `collapseProgress`; only an actual
  // chase gets held still until it resolves. The `showTitle` snap below is a deliberate,
  // already-animated transition rather than a scroll chase, so it opts out via `quietMs: 0`.
  const guardedCollapseProgress = useScrollProgressGuard(
    collapseProgress,
    animateTransition ? 0 : COLLAPSE_CHASE_COOLDOWN_MS,
  );

  // collapsible pages: scroll controls collapseProgress
  // showTitle=false pages: H1 always hidden (tab-level pages)
  // showTitle=true non-collapsible pages: H1 always visible (drill-down)
  // showTitle is only used by household pages: true = drill-down (H1 visible),
  // false = tab-level (H1 hidden). undefined = default (not a household page).
  const titleVisible = collapsible || showTitle === true;
  const isCollapsibleHeader = collapsible || showTitle !== undefined;

  useLayoutEffect(() => {
    if (showTitle === undefined && !collapsible) {
      // Static pages with no collapse logic at all — nothing to wire up.
      return undefined;
    }
    if (showTitle === undefined) {
      // Pure collapsible (no household-style override). Sync to current scroll
      // position so the initial render matches where the user already is.
      setAnimateTransition(false);
      setCollapseProgress(computeScrollProgress());
      return undefined;
    }
    if (titleVisible) {
      // H1 should be visible — snap to expanded without animation
      setAnimateTransition(false);
      setCollapseProgress(0);
      return undefined;
    }
    // H1 should be hidden — animate the collapse so it doesn't snap shut
    setAnimateTransition(true);
    setCollapseProgress(1);
    const t = setTimeout(() => setAnimateTransition(false), 250);
    return () => clearTimeout(t);
  }, [pathname, titleVisible, showTitle, collapsible]);

  // `titleRowActions` is mounted in the breadcrumb row and translated down to the H1 row as the
  // header expands, so the node keeps one tree position across the collapse. The offset is measured
  // rather than hardcoded because it depends on the breadcrumb row's height, the actions' own
  // height, and how much room the bell takes — none of which the template can assume.
  const titleRowActionsRef = useRef<HTMLDivElement | null>(null);
  const expandedActionsAnchorRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const actions = titleRowActionsRef.current;
    const anchor = expandedActionsAnchorRef.current;
    if (!actions || !anchor) return;

    // Viewport rects rather than offsetLeft/offsetTop: the anchor is absolute inside the H1 row, so
    // the two nodes have different offsetParents and their offsets aren't comparable. The transform
    // is cleared first because a rect already includes it — reading it back would compound. Both
    // happen inside one layout effect, so the browser paints the result only once.
    actions.style.transform = '';
    const from = actions.getBoundingClientRect();
    const to = anchor.getBoundingClientRect();
    const dx = to.left - from.right;
    const dy = to.top - (from.top + from.height / 2);
    const remaining = 1 - guardedCollapseProgress;

    // `will-change-transform` below puts this cluster on its own render layer.
    // The browser blurs a layer that sits between two screen pixels, so round to whole ones.
    const dpr = window.devicePixelRatio || 1;
    const tx = Math.round(dx * remaining * dpr) / dpr;
    const ty = Math.round(dy * remaining * dpr) / dpr;

    actions.style.transform = tx === 0 && ty === 0 ? '' : `translate(${tx}px, ${ty}px)`;
    actions.style.transition = animateTransition ? 'transform 200ms ease-in-out' : '';
  });

  const handleScroll = useCallback(() => {
    setAnimateTransition(false);
    setCollapseProgress(computeScrollProgress());
  }, []);

  useEffect(() => {
    if (!collapsible) return;
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [collapsible, handleScroll]);

  // Scroll anchoring cannot coexist with a header whose height is a function of the scroll offset:
  // it answers the height this bar gives up by moving the offset that `collapseProgress` reads back,
  // so on a page that is only just scrollable the two chase each other every frame and the header
  // visibly shakes. Scoped to the whole mount rather than to mid-collapse because the oscillation
  // steps through both ends of the collapse, and anything narrower re-arms it on alternating frames.
  useEffect(() => {
    if (!collapsible) return undefined;
    const scroller = document.scrollingElement;
    if (!(scroller instanceof HTMLElement)) return undefined;
    const previous = scroller.style.overflowAnchor;
    scroller.style.overflowAnchor = 'none';
    return () => {
      scroller.style.overflowAnchor = previous;
    };
  }, [collapsible]);

  // The H1 is "gone" (and the leaf should appear in the breadcrumb bar) once
  // the user has scrolled past the collapse distance.
  const isFullyCollapsed = guardedCollapseProgress >= 1;

  const iconElement = Icon ? (
    <HoverCard>
      <HoverCardTrigger render={<Icon className="size-4 text-secondary" />} />
      {iconHoverContent && <HoverCardContent side="right">{iconHoverContent}</HoverCardContent>}
    </HoverCard>
  ) : null;

  // `gridTemplateRows: ${1 - p}fr` is the standard collapse trick: the inner overflow-hidden child
  // clamps to whatever fraction of its natural height the grid row allots, so the block shrinks
  // smoothly rather than snapping.
  const collapseGridStyle = () => ({
    gridTemplateRows: `${1 - guardedCollapseProgress}fr`,
    opacity: 1 - guardedCollapseProgress,
    transition: animateTransition ? 'grid-template-rows 200ms ease-in-out, opacity 200ms ease-in-out' : undefined,
  });

  const renderCollapsibleContent = () => {
    // Trail shown above the H1 when expanded: parent segments only.
    // The H1 itself stands in for the leaf, so dropping it from the
    // trail keeps a single `aria-current` source on the page.
    const expandedTrail = breadcrumbs?.slice(0, -1) ?? [];
    const verticalPadding =
      PADDING_EXPANDED_PX - (PADDING_EXPANDED_PX - PADDING_COLLAPSED_PX) * guardedCollapseProgress;
    const titleBlock = (
      <div className="grid" style={collapseGridStyle()}>
        <div className="overflow-hidden w-full">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 w-full gap-y-2">
            <div className="flex items-baseline gap-2">
              {loading ? (
                <Skeleton className="h-11 w-64" />
              ) : (
                <>
                  <Text variant="h1" render={<h1 />}>
                    {pageTitle}
                  </Text>
                  {pageTitleSuffix && (
                    <Text variant="h2" color="secondary" render={<span />}>
                      {pageTitleSuffix}
                    </Text>
                  )}
                </>
              )}
              {iconElement}
            </div>
            {pageTitleExtra && <div className="flex items-center gap-2">{pageTitleExtra}</div>}
          </div>
          {description && <Text variant="body-sm">{description}</Text>}
        </div>
      </div>
    );
    return (
      <div className="flex justify-between items-center w-full">
        <div
          className="w-full"
          style={{
            paddingTop: `${verticalPadding}px`,
            paddingBottom: `${verticalPadding}px`,
            transition: animateTransition ? 'padding 200ms ease-in-out' : undefined,
          }}
        >
          {/* Breadcrumbs (left) + titleRowActions and the notification bell (right) share a row so
              the bell always sits flush with the breadcrumb, regardless of whether the H1 below is
              expanded or collapsed.

              titleRowActions has its one and only mount here, for both collapse states, and is
              *translated* to the H1 row while expanded rather than rendered there. Rendering it
              from two sites read as moving it, but React reconciles by tree position, so crossing
              the threshold unmounted one subtree and mounted the other — discarding the cluster's
              state mid-scroll, which lost an open note editor's unflushed autosave, closed
              half-filled dialogs, and destroyed keyboard focus. Living in this row also means its
              layout box never enters or leaves flow, which is what keeps the header height
              continuous instead of stepping ~48px at the threshold. */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <BreadcrumbSegments
                segments={isFullyCollapsed ? (breadcrumbs ?? []) : expandedTrail}
                markLastAsCurrent={isFullyCollapsed}
              />
              {isFullyCollapsed && iconElement}
              {breadcrumbRowExtra && (
                // `flex-1 min-w-0` hands it the row's leftover width and lets it shrink to nothing,
                // so the trail keeps its natural width and a long household name is never squeezed
                // by however many tags the household happens to carry.
                <div className="flex min-w-0 flex-1 items-center">
                  <PageTitleCollapsedContext.Provider value={isFullyCollapsed}>
                    {breadcrumbRowExtra}
                  </PageTitleCollapsedContext.Provider>
                </div>
              )}
            </div>
            {(titleRowActions || headerSlot) && (
              <div className="flex items-center gap-2">
                {titleRowActions && (
                  // z-10 because the transform below lands these actions on top of the title row,
                  // which is a later sibling and so paints above them by default — without it the
                  // whole cluster is unclickable until the header collapses and the transform clears.
                  <div ref={titleRowActionsRef} className="relative z-10 flex items-center gap-2 will-change-transform">
                    <PageTitleCollapsedContext.Provider value={isFullyCollapsed}>
                      {titleRowActions}
                    </PageTitleCollapsedContext.Provider>
                  </div>
                )}
                {headerSlot}
              </div>
            )}
          </div>
          {titleRowActions ? (
            <div className="relative flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">{titleBlock}</div>
              {/* Marks where the actions belong while expanded. Absolute and zero-sized so it adds
                  no height of its own — an in-flow marker's own margin would floor this row and
                  leave the collapsed header permanently taller. */}
              <div
                ref={expandedActionsAnchorRef}
                aria-hidden
                className="absolute right-0 h-0 w-0"
                style={{ top: `calc(50% + ${ACTIONS_TITLE_ROW_NUDGE_PX}px)` }}
              />
            </div>
          ) : (
            titleBlock
          )}
        </div>
        {actionButtons && <div className="flex items-center gap-2">{actionButtons}</div>}
      </div>
    );
  };

  const renderDefaultContent = () => (
    <div className="flex justify-between items-center">
      <div className="py-4">
        <div className="flex items-baseline gap-2">
          {breadcrumbs && breadcrumbs.length > 0 ? (
            <BreadcrumbSegments segments={breadcrumbs} />
          ) : loading ? (
            <Skeleton className="h-11 w-64" />
          ) : (
            <>
              <Text variant="h1" render={<h1 />}>
                {pageTitle}
              </Text>
              {pageTitleSuffix && (
                <Text variant="h2" color="secondary" render={<span />}>
                  {pageTitleSuffix}
                </Text>
              )}
            </>
          )}
          {iconElement}
        </div>
        {description && <Text variant="body-sm">{description}</Text>}
      </div>
      <div className="flex items-center gap-2">
        {titleRowActions}
        {headerSlot}
        {actionButtons}
      </div>
    </div>
  );

  // Track PageTitle height via ResizeObserver and publish as CSS variable
  // so downstream sticky elements (e.g. managed investments navbar) can use it.
  const innerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry) {
        document.documentElement.style.setProperty('--page-title-height', `${entry.contentRect.height}px`);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Merge forwarded ref with our internal ref
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      innerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [ref],
  );

  return (
    <>
      <Head>
        <title>{headTitle}</title>
      </Head>
      <div ref={setRefs} className="SavvyPageTitleContainer sticky top-0 z-20 w-full bg-slate-50 border-b border-light">
        <div className="flex justify-center px-8">
          <div className="w-full" style={{ maxWidth: maxWidth ?? '1400px' }}>
            {isCollapsibleHeader ? renderCollapsibleContent() : renderDefaultContent()}
            {collapseBelowHeader && isCollapsibleHeader && belowHeader ? (
              // Hidden from AT as well as from view: `breadcrumbRowExtra` is the collapsed
              // counterpart of this content, so leaving the clipped copy in the tree would have a
              // screen reader read both.
              <div className="grid" style={collapseGridStyle()} aria-hidden={isFullyCollapsed} inert={isFullyCollapsed}>
                <div className="overflow-hidden">{belowHeader}</div>
              </div>
            ) : (
              belowHeader
            )}
            {hasTabs && (
              <div className={cn('flex items-center gap-2', tabVariant === TAB_VARIANT_TOGGLE ? 'pt-1 pb-5' : 'pt-1')}>
                <div className="min-w-0 flex-1">
                  <HorizontalSlider dependencies={[tabsProps?.tabs, tabsProps?.activeTab]}>
                    {tabVariant === TAB_VARIANT_TOGGLE ? <ToggleTabs {...tabsProps} /> : <LinkTabs {...tabsProps} />}
                  </HorizontalSlider>
                </div>
                {tabRowActions && <div className="flex shrink-0 items-center gap-2">{tabRowActions}</div>}
              </div>
            )}
            {children}
          </div>
        </div>
      </div>
    </>
  );
});

export default PageTitle;
export type { PageTitleProps, BreadcrumbSegment };
